import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import cogoToast from "cogo-toast";
import httpRequest from "../../../../../ajax";
import ShareButton from "../ShareButton";
import CustomBadge from "../../../../../components/CustomBadge";

/**
 * LeaderboardWidget — WB League table on the Workbrook feed tab.
 *
 * Per PRD v1.0 (Point-Based Scoring Update):
 *   - Ranks by total points: (invite_count * 1) + (placement_count * 20)
 *   - Shows tier badges (Bronze/Silver/Gold/Platinum) based on points
 *   - Monthly and all-time toggle
 *   - "See all" navigates to /league/list full page
 *   - Empty state: "The league starts here" placeholder
 *   - User not ranked: "Not ranked yet. Make your first referral to join the league."
 */
const LeaderboardWidget = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("monthly");
  const [optedIn, setOptedIn] = useState(false);
  const [optingIn, setOptingIn] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleOptIn = async (optIn = true) => {
    setOptingIn(true);
    try {
      const res = await httpRequest("put", "/leaderboard/opt-in", { opt_in: optIn });
      if (res?.data?.status) {
        setOptedIn(optIn);
        queryClient.invalidateQueries('wbLeague');
        fetchLeaderboard(period);
      }
    } catch (err) {
      console.error("Opt-in failed:", err);
    } finally {
      setOptingIn(false);
    }
  };

  const fetchLeaderboard = useCallback(
    async (selectedPeriod) => {
      setLoading(true);
      try {
        const res = await httpRequest("get", `/leaderboard?period=${selectedPeriod}`);
        if (res?.data?.status) {
          setData(res.data.data);
          setOptedIn(res.data.data.user_opted_in || false);
        }
      } catch (err) {
        console.error("LeaderboardWidget: fetch error", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchLeaderboard(period);
  }, [period, fetchLeaderboard]);

  const handlePeriodToggle = (newPeriod) => {
    if (newPeriod !== period) {
      setPeriod(newPeriod);
    }
  };

  const handleSeeAll = () => {
    history.push("/league/list");
  };

  const tierStyles = {
    bronze:   { bg: "#f5e6d3", color: "#8b4513", label: "BRONZE",   icon: "🥉" },
    silver:   { bg: "#e8e8e8", color: "#555",    label: "SILVER",   icon: "🥈" },
    gold:     { bg: "#fef3c7", color: "#a16207", label: "GOLD",     icon: "🥇" },
    platinum: { bg: "#e0f2fe", color: "#0369a1", label: "PLATINUM", icon: "💎" },
  };

  const topReferrers = data?.top_referrers || [];
  const totalReferrers = data?.total_referrers || 0;
  const userRank = data?.user_rank;
  const userPoints = data?.user_points || 0;
  const userInviteCount = data?.user_invite_count || 0;
  const userPlacementCount = data?.user_placement_count || 0;
  const userTier = data?.user_tier;
  const userOptedIn = data?.user_opted_in || false;

  // Build display rows: actual referrers + "Open spot" fillers
  const displayRows = [];
  for (let i = 0; i < 5; i++) {
    if (topReferrers[i]) {
      displayRows.push({ type: "referrer", data: topReferrers[i] });
    } else {
      displayRows.push({ type: "open", rank: i + 1 });
    }
  }

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3a6ed4" strokeWidth="2">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
          <span style={styles.title}>WB League</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* ── Period Toggle ── */}
          <div style={styles.toggle}>
            <button
              style={period === "monthly" ? styles.toggleBtnActive : styles.toggleBtn}
              onClick={() => handlePeriodToggle("monthly")}
            >
              Monthly
            </button>
            <button
              style={period === "alltime" ? styles.toggleBtnActive : styles.toggleBtn}
              onClick={() => handlePeriodToggle("alltime")}
            >
              All-time
            </button>
          </div>
          <ShareButton
            url={`${window.location.origin}/league`}
            title="Workbrook League"
            text="Check out the Workbrook League leaderboard!"
          />
        </div>
      </div>

      {/* ── Consent Banner ── */}
      {!loading && !optedIn && !dismissed && (
        <div style={styles.consentBanner}>
          <span style={styles.consentIcon}>🔒</span>
          <span style={styles.consentText}>
            Your name and photo are hidden. Show your identity on the leaderboard?
          </span>
          <button
            style={styles.consentBtn}
            onClick={() => handleOptIn(true)}
            disabled={optingIn}
          >
            {optingIn ? "..." : "Show my identity"}
          </button>
          <button
            style={{ ...styles.consentBtn, background: "transparent", color: "#64748b", border: "1px solid #cbd5e1" }}
            onClick={() => {
              setDismissed(true);
              cogoToast.info(
                "You can change your leaderboard visibility anytime from your Profile settings.",
                { position: "top-center", heading: "Visibility Reminder", hideAfter: 5 }
              );
            }}
          >
            Maybe later
          </button>
        </div>
      )}
      {!loading && optedIn && (
        <div style={{ ...styles.consentBanner, backgroundColor: "#f0fdf4", borderColor: "#bbf7d0" }}>
          <span style={styles.consentIcon}>✅</span>
          <span style={styles.consentText}>
            Your name and photo are visible on the leaderboard.
          </span>
          <button
            style={{ ...styles.consentBtn, background: "#64748b" }}
            onClick={() => handleOptIn(false)}
            disabled={optingIn}
          >
            {optingIn ? "..." : "Hide my identity"}
          </button>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={styles.loading}>
          <div style={styles.loadingSpinner} />
        </div>
      )}

      {/* ── Body ── */}
      {!loading && (
        <>
          {/* Empty state: no referrers with points at all */}
          {totalReferrers === 0 ? (
            <div style={styles.emptyState}>
              {displayRows.map((row, i) => (
                <div key={i} style={styles.blurredRow} />
              ))}
              <p style={styles.emptyText}>
                The league starts here. Refer your first candidate to a Tier 2 or Tier 3 job to claim a spot.
              </p>
            </div>
          ) : (
            /* Normal: show referrers + open spots in WB League table style */
            <div style={styles.list}>
              {displayRows.map((row, i) => {
                if (row.type === "open") {
                  return (
                    <div key={i} style={styles.openRow}>
                      <span style={styles.rank}>#{row.rank}</span>
                      <div style={styles.userInfo}>
                        <div style={styles.avatarPlaceholder}>
                          <svg style={styles.avatarPlaceholderIcon} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        </div>
                        <div style={styles.userDetails}>
                          <span style={styles.openText}>Open spot — could be you</span>
                        </div>
                      </div>
                    </div>
                  );
                }
                const ts = tierStyles[row.data.tier] || tierStyles.bronze;
                const points = row.data.points || 0;
                const invites = row.data.invite_count || 0;
                const placements = row.data.placement_count || 0;
                const isAnon = row.data.is_anonymized == 1;
                const displayName = row.data.display_name || "Referrer_0000";
                return (
                  <div key={i} style={styles.referrerRow}>
                    <span style={styles.rank}>#{row.data.rank}</span>
                    <div style={styles.userInfo}>
                      {isAnon ? (
                        <div style={styles.avatarPlaceholder}>
                          <svg style={styles.avatarPlaceholderIcon} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        </div>
                      ) : (
                        <img
                          src={row.data.image_url}
                          alt="avatar"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "";
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <div style={styles.userDetails}>
                        <h6 style={styles.name}>{displayName}</h6>
                        <span style={{ ...styles.tierBadge, backgroundColor: ts.bg, color: ts.color, display: "inline-flex", alignItems: "center", gap: "3px" }}>
                          <span style={styles.tierIcon}>{ts.icon}</span>
                          {ts.label}
                        </span>
                      </div>
                    </div>
                    <div style={styles.pointsSection}>
                      <CustomBadge figure={String(points)} />
                      <span style={styles.pointsLabel}>
                        {invites} invite{invites === 1 ? "" : "s"} · {placements} placement{placements === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── See More button ── */}
          {totalReferrers > 5 && (
            <button
              style={styles.seeMoreBtn}
              onClick={handleSeeAll}
            >
              See More →
            </button>
          )}

          {/* ── User's own rank ── */}
          {!loading && (
            <div style={styles.userRankSection}>
              {userPoints === 0 ? (
                <div style={styles.userNotRanked}>
                  <span style={styles.aspiringText}>Aspiring Referrer</span>
                  <a href="/home" style={styles.referCta}>Make your first referral →</a>
                </div>
              ) : userRank ? (
                <div style={styles.userRanked}>
                  <span style={styles.userRankLabel}>
                    You #{userRank.rank}
                  </span>
                  <span style={{ ...styles.tierBadge, ...(tierStyles[userTier] || tierStyles.bronze) }}>
                    {(tierStyles[userTier] || tierStyles.bronze).label}
                  </span>
                  <span style={styles.count}>
                    {userPoints} pts · {userInviteCount} invite{userInviteCount === 1 ? "" : "s"} · {userPlacementCount} placement{userPlacementCount === 1 ? "" : "s"}
                  </span>
                </div>
              ) : (
                <div style={styles.userNotRanked}>
                  <span style={styles.aspiringText}>
                    Not ranked yet this {period === "monthly" ? "month" : "period"}.
                  </span>
                  <a href="/jobs" style={styles.referCta}>Refer now →</a>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    padding: "18px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
  },
  toggle: {
    display: "flex",
    gap: "4px",
    backgroundColor: "#f1f5f9",
    borderRadius: "8px",
    padding: "2px",
  },
  toggleBtn: {
    background: "none",
    border: "none",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    cursor: "pointer",
    borderRadius: "6px",
  },
  toggleBtnActive: {
    background: "#fff",
    border: "none",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#3a6ed4",
    cursor: "pointer",
    borderRadius: "6px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "30px",
  },
  loadingSpinner: {
    width: "24px",
    height: "24px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#3a6ed4",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  referrerRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  openRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 0",
    borderBottom: "1px solid #f1f5f9",
    opacity: 0.6,
  },
  rank: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#94a3b8",
    minWidth: "28px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
  },
  avatarPlaceholder: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#e2e8f0",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarPlaceholderIcon: {
    width: "22px",
    height: "22px",
    color: "#94a3b8",
  },
  userDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  name: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
    margin: 0,
  },
  openText: {
    fontSize: "13px",
    color: "#94a3b8",
    fontStyle: "italic",
  },
  tierBadge: {
    fontSize: "10px",
    fontWeight: "700",
    padding: "2px 8px",
    borderRadius: "4px",
    letterSpacing: "0.3px",
    display: "inline-block",
    width: "fit-content",
  },
  tierIcon: {
    fontSize: "11px",
  },
  pointsSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
  },
  pointsLabel: {
    fontSize: "10px",
    color: "#94a3b8",
  },
  count: {
    fontSize: "12px",
    color: "#64748b",
  },
  emptyState: {
    padding: "10px 0",
  },
  blurredRow: {
    height: "48px",
    borderRadius: "6px",
    backgroundColor: "#f1f5f9",
    marginBottom: "8px",
    filter: "blur(2px)",
  },
  emptyText: {
    fontSize: "13px",
    color: "#94a3b8",
    textAlign: "center",
    marginTop: "12px",
    lineHeight: 1.5,
  },
  userRankSection: {
    marginTop: "14px",
    paddingTop: "14px",
    borderTop: "1px solid #f1f5f9",
  },
  seeMoreBtn: {
    width: "100%",
    background: "none",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#3a6ed4",
    cursor: "pointer",
    marginTop: "10px",
    transition: "background-color 0.15s",
  },
  userRanked: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  userRankLabel: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#3a6ed4",
  },
  userNotRanked: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  aspiringText: {
    fontSize: "13px",
    color: "#94a3b8",
  },
  referCta: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#3a6ed4",
    textDecoration: "none",
  },
  consentBanner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "8px",
    padding: "10px 14px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },
  consentIcon: {
    fontSize: "18px",
    flexShrink: 0,
  },
  consentText: {
    fontSize: "12px",
    color: "#475569",
    flex: 1,
    minWidth: "150px",
  },
  consentBtn: {
    background: "#3a6ed4",
    border: "none",
    borderRadius: "6px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
};

export default LeaderboardWidget;
